import { useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { CATEGORIES, clearVendorCookies, OPT_IN_CATEGORIES, useConsent } from '@src/corelib/consent';

const CATEGORY_COPY = [
  {
    key: CATEGORIES.NECESSARY,
    title: 'Strictly necessary',
    body: 'Required to deliver the site: session handling, security tokens, spam protection, and remembering this choice. These cannot be switched off.',
    locked: true,
  },
  {
    key: CATEGORIES.ANALYTICS,
    title: 'Analytics',
    body: 'Sentry session replay, used to diagnose errors and usability problems. Text is masked before it is recorded.',
    locked: false,
  },
  {
    key: CATEGORIES.MARKETING,
    title: 'Marketing',
    body: 'Microsoft Clarity, which records session activity and heatmaps and shares an advertising identifier with Bing.',
    locked: false,
  },
];

function ConsentPreferences({ open, onClose }) {
  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={onClose}>
      <DialogTitle>Cookie preferences</DialogTitle>
      {/*
        Rendered only while open, so the form's state initialiser re-runs on
        every open and the draft always starts from the stored categories. That
        replaces a useEffect that synced draft state on `open` — calling
        setState from an effect body cascades renders (react-hooks/set-state-in-effect).
      */}
      {open && <PreferencesForm onClose={onClose} />}
    </Dialog>
  );
}

function PreferencesForm({ onClose }) {
  const { categories, save, state } = useConsent();

  // Opt-in categories are never pre-ticked: with no prior decision, `categories`
  // is necessary-only, so both switches start off.
  const [draft, setDraft] = useState(() => ({ ...categories }));

  const handleSave = () => {
    // Every opt-in category is checked, not just marketing: analytics injects
    // Sentry session replay via addIntegration, which is no more removable than
    // Clarity's script tag. Reloading only on marketing loss left replay
    // recording for the rest of the page load after analytics was switched off.
    const previous = state?.categories ?? {};
    const revoking = OPT_IN_CATEGORIES.some((category) => previous[category] && !draft[category]);

    save(draft);
    onClose();

    // An injected tag cannot be removed, so revoking consent is only honoured by
    // clearing the vendor cookies we can reach and then reloading.
    // `save` does not clear them (only `withdraw` does), so this partial-revoke
    // path has to do both explicitly.
    if (revoking) {
      clearVendorCookies();
      window.location.reload();
    }
  };

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={3}>
          {CATEGORY_COPY.map(({ key, title, body, locked }) => (
            <Stack key={key} spacing={0.5}>
              <FormControlLabel
                label={<Typography fontWeight={600}>{title}</Typography>}
                control={
                  <Switch
                    disabled={locked}
                    checked={locked ? true : Boolean(draft[key])}
                    onChange={(event) => setDraft((prev) => ({ ...prev, [key]: event.target.checked }))}
                  />
                }
              />

              <Typography sx={{ pl: 6 }} variant="body2" color="text.secondary">
                {body}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save preferences</Button>
      </DialogActions>
    </>
  );
}

export default ConsentPreferences;
